/**
 * IssuesFacetSidebar — Facetten (Issue-Typ, Schweregrad) als
 * aria-pressed Toggle-Buttons in role="group" (kein tablist).
 */
import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/utils'
import type { RuleCoverageEntry } from '@/lib/api/issues-insights'

interface IssuesFacetSidebarProps {
  typeCounts: Record<string, number>
  severityCounts: Record<string, number>
  activeTypes: Set<string>
  activeSeverities: Set<string>
  onToggleType: (v: string) => void
  onToggleSeverity: (v: string) => void
  coverage: RuleCoverageEntry[]
  activeChannel: string | null
  onSelectChannel: (id: string | null) => void
  onSetupRules: (channelId: string) => void
}

interface FacetGroupProps {
  label: string
  prefix: string
  counts: Record<string, number>
  active: Set<string>
  onToggle: (v: string) => void
  labelFor: (k: string) => string
}

function FacetGroup({ label, prefix, counts, active, onToggle, labelFor }: FacetGroupProps) {
  return (
    <div role="group" aria-label={label} className="flex flex-col gap-1">
      <span className="text-xs font-semibold uppercase text-neutral-400">{label}</span>
      {Object.entries(counts).map(([k, n]) => {
        const on = active.has(k)
        return (
          <button
            key={k}
            type="button"
            data-testid={`${prefix}-${k}`}
            aria-pressed={on}
            onClick={() => onToggle(k)}
            className={cn(
              'flex items-center justify-between rounded-lg px-3 py-1.5 text-sm transition-colors',
              on
                ? 'bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-400'
                : 'text-neutral-600 hover:bg-neutral-100 dark:text-neutral-400 dark:hover:bg-neutral-800'
            )}
          >
            <span>{labelFor(k)}</span>
            <span className="text-xs text-neutral-400">{n}</span>
          </button>
        )
      })}
    </div>
  )
}

interface ChannelFacetGroupProps {
  coverage: RuleCoverageEntry[]
  activeChannel: string | null
  onSelectChannel: (id: string | null) => void
  onSetupRules: (channelId: string) => void
}

function ChannelFacetGroup({ coverage, activeChannel, onSelectChannel, onSetupRules }: ChannelFacetGroupProps) {
  const { t } = useTranslation()
  return (
    <div role="group" aria-label={t('productsIssues.workList.facetChannels')} className="flex flex-col gap-1">
      <span className="text-xs font-semibold uppercase text-neutral-400">
        {t('productsIssues.workList.facetChannels')}
      </span>
      {coverage.map((c) =>
        c.rules_count > 0 ? (
          <button
            key={c.channel_id}
            type="button"
            data-testid={`facet-channel-${c.channel_id}`}
            aria-pressed={activeChannel === c.channel_id}
            onClick={() => onSelectChannel(activeChannel === c.channel_id ? null : c.channel_id)}
            className={cn(
              'flex items-center justify-between rounded-lg px-3 py-1.5 text-sm transition-colors',
              activeChannel === c.channel_id
                ? 'bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-400'
                : 'text-neutral-600 hover:bg-neutral-100 dark:text-neutral-400 dark:hover:bg-neutral-800'
            )}
          >
            <span className="truncate">{c.name}</span>
            <span className="ml-2 flex shrink-0 items-center gap-1.5 text-xs text-neutral-400">
              <span>{t('productsIssues.workList.rulesChip', { count: c.rules_count })}</span>
              <span>{c.open_issues}</span>
            </span>
          </button>
        ) : (
          <button
            key={c.channel_id}
            type="button"
            data-testid={`facet-channel-cta-${c.channel_id}`}
            onClick={() => onSetupRules(c.channel_id)}
            className="rounded-lg border border-dashed border-neutral-300 px-3 py-1.5 text-left text-sm text-neutral-500 hover:border-primary-400 hover:text-primary-600 dark:border-neutral-700"
          >
            <span className="block font-medium">{c.name}</span>
            <span className="block text-xs">{t('productsIssues.workList.noRulesCta')}</span>
          </button>
        )
      )}
    </div>
  )
}

export function IssuesFacetSidebar(props: IssuesFacetSidebarProps) {
  const { t } = useTranslation()
  const sevLabel = (k: string) =>
    t(`productsIssues.workList.severity${k.charAt(0).toUpperCase()}${k.slice(1)}`, k)

  return (
    <aside className="flex w-56 flex-shrink-0 flex-col gap-4">
      <ChannelFacetGroup
        coverage={props.coverage}
        activeChannel={props.activeChannel}
        onSelectChannel={props.onSelectChannel}
        onSetupRules={props.onSetupRules}
      />
      <FacetGroup
        label={t('productsIssues.workList.facetType')}
        prefix="facet-type"
        counts={props.typeCounts}
        active={props.activeTypes}
        onToggle={props.onToggleType}
        labelFor={(k) => k}
      />
      <FacetGroup
        label={t('productsIssues.workList.facetSeverity')}
        prefix="facet-severity"
        counts={props.severityCounts}
        active={props.activeSeverities}
        onToggle={props.onToggleSeverity}
        labelFor={sevLabel}
      />
    </aside>
  )
}
